import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Key, User, ArrowLeft, Loader2, CheckCircle2, Link2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { useDispatch, useSelector } from 'react-redux';
import { fetchQuestions } from '../../store/slices/securityQuestionSlice';
import securityQuestionApi from '../../api/securityQuestionApi';

const SecurityQuestionLoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { questions, isLoading: qLoading, error: qError } = useSelector(
    (state) => state.securityQuestion
  );

  const [employeeCode, setEmployeeCode] = useState('');
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [verified, setVerified] = useState(false);
  const [resetLink, setResetLink] = useState('');

  useEffect(() => {
    dispatch(fetchQuestions());
  }, [dispatch]);

  useEffect(() => {
    if (qError) toast.error(qError);
  }, [qError]);

  const validate = () => {
    const e = {};
    if (!employeeCode.trim()) e.employeeCode = 'Employee code is required';
    questions.forEach((q) => {
      if (!answers[q.id]?.trim()) e[`answer_${q.id}`] = 'Answer required';
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setErrors({});

    try {
      const answersPayload = Object.entries(answers).map(([question_id, answer]) => ({
        question_id,
        answer
      }));

      const response = await securityQuestionApi.verify(employeeCode, answersPayload);

      // ---------------------------------------------------------
      // FIX: Check if API explicitly returned success: false
      // ---------------------------------------------------------
      if (response && response.success === false) {
        throw {
          response: {
            data: response
          }
        };
      }

      // ---------------------------------------------------------
      // HANDLE SUCCESS & GENERATE LINK
      // Response: { success: true, data: { reset_token: "..." }, ... }
      // ---------------------------------------------------------
      if (response.success && response.data?.reset_token) {
        const token = response.data.reset_token;

        // Construct the link. 
        // Note: We don't have the email here unless we passed it from the previous page.
        // Since ResetPasswordPage uses the token for verification, we can omit ?email=...
        const generatedLink = `/reset-password/${token}`;

        setResetLink(generatedLink);
        setVerified(true);
        toast.success('User verified!');

        // Clear form
        setEmployeeCode('');
        setAnswers({});
      } else {
        throw new Error('Verification successful, but no reset token received.');
      }

    } catch (err) {
      console.error(err);
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Verification failed.';

      toast.error(msg);
      setErrors({ general: msg });
    } finally {
      setSubmitting(false);
    }
  };

  if (qLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/30">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading questions…</span>
      </div>
    );
  }

  // ---------------------------------------------------------
  // SUCCESS VIEW: Show Link
  // ---------------------------------------------------------
  if (verified && resetLink) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-muted/30">
        <div className="w-full max-w-md text-center animate-fade-in">
          <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-600">
            <CheckCircle2 className="h-8 w-8" />
          </div>

          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl">Verification Successful</CardTitle>
              <CardDescription>
                You can now reset your password
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* <div className="p-4 bg-muted/50 rounded-lg border border-border break-all text-left">
                <p className="text-xs text-muted-foreground mb-1">Generated Link:</p>
                <p className="text-sm font-mono text-primary">{resetLink}</p>
              </div> */}

              <Button
                className="w-full"
                size="lg"
                onClick={() => navigate(resetLink)}
              >
                <Link2 className="mr-2 h-4 w-4" />
                Open Password Reset Link
              </Button>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => navigate('/login')}
              >
                Back to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------
  // MAIN FORM VIEW
  // ---------------------------------------------------------
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-muted/30">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8 animate-fade-in">
          <h1 className="text-3xl font-bold font-['Space_Grotesk'] mb-2 text-black">
            Security Verification
          </h1>
          <p className="text-muted-foreground text-black">
            Answer all security questions to reset the password
          </p>
        </div>

        <Card className="shadow-xl animate-slide-in">
          <CardHeader>
            <CardTitle className="text-2xl">Verify Your Identity</CardTitle>
            <CardDescription>
              Enter employee code + answer to every question
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Employee Code */}
              <div className="space-y-2">
                <Label htmlFor="employeeCode">Employee Code</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="employeeCode"
                    placeholder="e.g., EMP001"
                    value={employeeCode}
                    onChange={(e) => setEmployeeCode(e.target.value)}
                    className={cn('pl-10', errors.employeeCode && 'border-destructive')}
                  />
                </div>
                {errors.employeeCode && (
                  <p className="text-sm text-destructive">{errors.employeeCode}</p>
                )}
              </div>

              {/* Questions Map */}
              {questions.map((q) => (
                <div key={q.id} className="space-y-2">
                  <Label>{q.question}</Label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Your answer"
                      value={answers[q.id] || ''}
                      onChange={(e) =>
                        setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))
                      }
                      className={cn(
                        'pl-10',
                        errors[`answer_${q.id}`] && 'border-destructive'
                      )}
                    />
                  </div>
                  {errors[`answer_${q.id}`] && (
                    <p className="text-sm text-destructive">{errors[`answer_${q.id}`]}</p>
                  )}
                </div>
              ))}

              {errors.general && (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                  <p className="text-sm text-destructive">{errors.general}</p>
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={submitting || qLoading}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying…
                  </>
                ) : (
                  'Verify & Continue'
                )}
              </Button>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => navigate('/login')}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SecurityQuestionLoginPage;